import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, Renderer2, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { LiveAnnouncer } from '@angular/cdk/a11y';

import { provideValueAccessor } from './../../../utils/shared-utils';
import { InputType } from './input-type';
import { MheInputChangeEvent } from './models';


@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideValueAccessor(InputComponent)],
    standalone: false
})
export class InputComponent implements ControlValueAccessor, OnChanges  {
  @Input() disabled: boolean | null = null;
  @Input() error = false;
  @Input() feedback: string[] = [];
  @Input() id: string | null = null;
  @Input() instruction: string | null = null;
  @Input() label: string;
  @Input() maxlength: number | null = null;
  @Input() placeholder: string | null = null;
  @Input() success = false;
  @Input() type = InputType.Text;
  @Input() icon = '';
  @Input() inputAttributes: object = {};
  @Input() required = false;
  @Input() hideLabel = false;
  @Input() hiddenLabel: string | null = null;
  @Input() ariaDescribedBy: string | null = null;
  @Input() showFeedback: boolean = true;
  @Input() info: boolean = false;
  @Input() visualOnlyFeedback: boolean = false;

  private providedValue: any = null;

  @Input()
  get value(): any {
    return this.providedValue;
  }
  set value(value: any) {
    if (this.value !== value) {
      this.providedValue = value;
    }
  }

  @Output() inputChange: EventEmitter<MheInputChangeEvent> = new EventEmitter();
  @Output() inputFocus: EventEmitter<any> = new EventEmitter();
  @Output() inputBlur: EventEmitter<any> = new EventEmitter();

  @ViewChild('input', { static: true }) inputEl: ElementRef<HTMLInputElement>;
  @ViewChild('input', { static: true }) inputElRef: ElementRef<HTMLInputElement>;

  private isFeedbackAnnounced: boolean = false;

  focus() {
    this.inputEl.nativeElement.focus();
  }

  get hasFeedback() {
    const hasFeedback = (this.success || this.error || this.info) && this.showFeedback;

    if (hasFeedback && this.feedback.length > 0 && !this.isFeedbackAnnounced && !this.visualOnlyFeedback) {
      // Announce the feedback message for screen readers
      this.liveAnnouncer.announce(this.feedback.join(' '), 'assertive');
      this.isFeedbackAnnounced = true;
    }

    return hasFeedback;
  }

  get isInvalid() {
    if (this.success || this.error) {
      return this.success ? 'false' : 'true';
    }

    return undefined;
  }

  get hasIcon() {
    return this.icon !== '';
  }

  get helpBlockId() {
    return `help-block-${this.id}`;
  }

  get inputDescribedBy() {
    let describedBy = [];
    if (this.instruction) {
      describedBy.push('instruction-' + this.id);
    }
    if (this.hasFeedback) {
      describedBy.push(this.helpBlockId);
    }

    // Add external aria-describedby if provided
    if (this.ariaDescribedBy) {
      describedBy.push(this.ariaDescribedBy);
    }
    return describedBy.length > 0 ? describedBy.join(' ') : undefined;
  }

  constructor(private _changeDetectorRef: ChangeDetectorRef, private _renderer: Renderer2, private liveAnnouncer: LiveAnnouncer) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { inputAttributes } = changes;
    if (inputAttributes) {
      this.updateAttributes(inputAttributes);
    }
  }

  onInput(event: Event, value: string) {
    this.isFeedbackAnnounced = false;
    this.inputChange.emit({ event, value });
    this.onChange(value);
  }

  onBlur(event: Event, value: string) {
    this.inputBlur.emit({ event, value });
  }

  onChange = (_: any) => {};

  onTouch = (event: Event) => {
    this.inputFocus.emit({
      event,
      componentInstance: this,
    });
  };

  registerOnChange(fn: (value: any) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  updateAttributes(inputAttributes: SimpleChange) {
    const { currentValue, firstChange, previousValue } = inputAttributes;

    if (!firstChange && previousValue) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      Object.entries(previousValue).forEach(([key]) => {
        this._renderer.removeAttribute(this.inputElRef.nativeElement, `${key}`);
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.entries(currentValue).forEach(([key, value]) => {
      this._renderer.setAttribute(this.inputElRef.nativeElement, `${key}`, `${value}`);
    });
  }

  writeValue(val: any) {
    if (val !== this.value) {
      const normalizedValue = val == null ? '' : val;
      this.value = normalizedValue;
      this._changeDetectorRef.markForCheck();
    }
  }

  getInputElement(): HTMLInputElement {
    return this.inputEl.nativeElement;
  }
}
